// types/chatEnvelope.ts
//
// The app-context envelope: what the frontend tells the companion about where
// the student is standing when they send a turn (ORA-435 spec, "App-context
// envelope"). It is client-supplied, sanitized and size-capped here and again
// on the server, and every id in it is access-checked server-side.
//
// The envelope is data only. `deriveAppContextEnvelope` builds it, the central
// `useAppContextEnvelope` hook feeds that from the router and the study slices,
// and `sendChatTurn` puts it on the wire.

/**
 * Which chat shell the student is typing into. `document_split` was the
 * preclinical document overlay's chat, removed in ORA-736: nothing sends it
 * any more, and it stays in the type for study-set returns persisted before
 * then (the server still accepts it too).
 */
export type ChatSurface = 'chat_page' | 'drawer' | 'document_split';

/** The client shell sending the turn. */
export const CHAT_PLATFORMS = ['web', 'ios', 'android'] as const;
export type ChatPlatform = (typeof CHAT_PLATFORMS)[number];

/**
 * A named route id — never a path. Routes publish theirs as
 * `handle.chatRoute` in `src/routes/Routes.tsx`; anything without a handle
 * reports `other`.
 */
export type ChatRouteId =
  | 'dashboard'
  | 'performance'
  | 'chat'
  | 'study.qbank.block'
  | 'study.qbank.review'
  | 'study.flashcards'
  | 'study.video'
  | 'study.summary'
  | 'qbank.start'
  | 'qbank.today_session'
  | 'webinar'
  | 'self-assessments'
  | 'self-assessment.block'
  | 'topics'
  /** The Library home; an opened article reports `article`. */
  | 'library'
  | 'article'
  | 'videos'
  /** The Audio Overviews library (ORA-735). */
  | 'audio-overviews'
  | 'document'
  | 'flashcard-decks'
  | 'games'
  | 'help'
  | 'account'
  /** The Courses V1 Start screen and its fullscreen session. */
  | 'course'
  | 'course.session'
  | 'other';

/** Every route id, for validating route handles. */
export const CHAT_ROUTE_IDS: readonly ChatRouteId[] = [
  'dashboard',
  'performance',
  'chat',
  'study.qbank.block',
  'study.qbank.review',
  'study.flashcards',
  'study.video',
  'study.summary',
  'qbank.start',
  'qbank.today_session',
  'webinar',
  'self-assessments',
  'self-assessment.block',
  'topics',
  'library',
  'article',
  'videos',
  'audio-overviews',
  'document',
  'flashcard-decks',
  'games',
  'help',
  'account',
  'course',
  'course.session',
  'other',
];

/** Which run of a flashcard queue the student is in. */
export type ChatFlashcardScope = 'session' | 'lecture' | 'filters';

/**
 * What the student is doing, one kind at a time. The live details a surface
 * owns (a video's timestamp, a document's page window, a flashcard's position)
 * ride here rather than on the anchor, which stays a bare reference.
 */
export type ChatActivity =
  | {
      kind: 'qbank_block';
      blockId: string;
      /**
       * `review` once the block is submitted or the student is on the review
       * route — but never for a self-assessment attempt whose correctness stays
       * hidden after submission.
       */
      mode: 'study' | 'review';
      /** 1-based position in the block. Omitted when the block has no questions. */
      index?: number;
      total: number;
    }
  | {
      kind: 'flashcard_run';
      scope: ChatFlashcardScope | null;
      /** 1-based position in the run, clamped to `total`; 0 when it is empty. */
      position: number;
      total: number;
    }
  | {
      kind: 'video';
      videoId: string;
      /**
       * Playback position, floored to `CHAT_VIDEO_TIMESTAMP_BUCKET_SECONDS`.
       * A player publishes its exact position on every `timeupdate`; sending it
       * raw would rebuild the envelope several times a second and re-publish it
       * to every mounted surface, for a precision the companion cannot use.
       */
      timestampSec: number | null;
      /**
       * The media's total length in whole seconds, from the player (ORA-507).
       * Omitted when the player has not reported a finite length — a YouTube
       * embed, or a native video before its metadata loads — so the companion
       * can say "at 0:05 of 12:30" only when it actually knows the 12:30.
       */
      durationSec?: number;
      playing: boolean;
    }
  | { kind: 'article'; articleId: string; sectionId: string | null }
  | {
      kind: 'document';
      lectureId: string;
      /** Inclusive `[from, to]` page window currently on screen. */
      pages: [number, number] | null;
    }
  | { kind: 'today_session'; sessionId: string }
  | { kind: 'idle' };

/** The content kinds a turn can be anchored to. */
export type ChatAnchorType =
  'question' | 'flashcard' | 'video' | 'article' | 'lecture' | 'exam';

/**
 * The anchor as it goes on the wire: a reference plus a visibility hint.
 *
 * `hidden` is a narrowing hint any anchor may carry, and the only thing a
 * question anchor may ever say: reveal there is derived server-side from the
 * student's own submitted response, because a client-asserted reveal would be
 * a way to read the answer key out of a live block.
 *
 * `revealed` is for flashcards alone (ORA-459). A card has no submitted
 * response to derive anything from — flipping it is the whole interaction —
 * so the surface is the only thing that knows the back is on screen, and
 * without saying so the companion has to answer every flipped card as if the
 * student were still looking at the front.
 */
export type ChatAnchorRef = {
  type: ChatAnchorType;
  id: string;
  variantId?: string;
  visibility?: 'hidden' | 'revealed';
};

/**
 * What a surface publishes about the content the student is on. Richer than
 * `ChatAnchorRef`: the extra fields feed `activity`, and `revealed` is folded
 * into the narrowing visibility hint.
 *
 * `label` is what the composer's context pill reads (ORA-662): the publisher
 * already has the question number, the card front, the title or the file name
 * at hand, and nothing downstream can recover them. It is client-only and
 * never goes on the wire — the server computes its own `contextLabel` from the
 * ids during hydration.
 */
/**
 * Which content-type icon the composer's context pill draws for an anchor,
 * when its type's own icon is not the right one: a course start screen anchors
 * on the course exam but names the step the student is on, and wants that
 * step's icon. UI only; never sent.
 */
export type ChatAnchorIcon = 'reading' | 'video' | 'question' | 'flashcard';

export type ChatAnchorInput = { label?: string; icon?: ChatAnchorIcon } & (
  | {
      type: 'question';
      vignetteId: string;
      variantId?: string | null;
      revealed?: boolean;
    }
  | {
      type: 'flashcard';
      flashcardId: string;
      /** The exact variant the flashcard renderer selected for this review. */
      variantId?: string | null;
      revealed?: boolean;
    }
  | {
      type: 'video';
      videoId: string;
      timestampSec?: number | null;
      durationSec?: number | null;
      playing?: boolean;
    }
  | { type: 'article'; articleId: string; sectionId?: string | null }
  | { type: 'lecture'; lectureId: string; pages?: [number, number] | null }
  | { type: 'exam'; examId: string }
);

/**
 * The envelope itself. Declared as a type alias rather than an interface so it
 * stays assignable to `ChatTurnRequest['envelope']` (`Record<string, unknown>`)
 * without a cast — the stream client deliberately does not know this shape.
 */
export type ChatAppContextEnvelope = {
  surface: ChatSurface;
  route: ChatRouteId;
  /** Added at send time from Capacitor, after the surface publishes context. */
  platform?: ChatPlatform;
  /**
   * The browser path, sent for the Help route only. The server resolves the
   * help page it names so pages that must be reached through docs_search
   * (About Ora) are never pasted into the prompt off the screen (ORA-703).
   */
  path?: string;
  activity: ChatActivity;
  /** The side viewer, kept separate so it never changes activity or threading. */
  viewing?: ChatViewing;
  /** The course the student is in, when they are in one. Context only. */
  course?: ChatCourseContext;
  anchor?: ChatAnchorRef;
  /**
   * The student removed the on-screen item (or the page) from this chat with
   * the composer's context pill (ORA-662). `anchor` and `screen` are both
   * withheld when it is set, and the server says so in the envelope block
   * rather than letting the model assume nothing was on screen.
   */
  anchorDetached?: boolean;
  /** Text the student highlighted, trimmed and capped. */
  selection?: string;
  /** Text the composer was opened with, trimmed and capped. */
  prefill?: string;
  /**
   * A structured text outline of what the student's page is showing, captured
   * once per send and capped (ORA-629). Headings, tables, lists and controls
   * keep their shape, so the companion can answer "what does this number mean"
   * about a figure it would otherwise never see. Never attached from the chat
   * page (there is no page behind it) or while a self-assessment is live.
   */
  screen?: string;
};

export type ChatViewing =
  | {
      kind: 'chat_document';
      documentId: string;
      pages: [number, number] | null;
    }
  | {
      kind: 'lecture';
      lectureId: string;
      pages: [number, number] | null;
    };

/**
 * Where in a course the student is (Courses V1, COMPANION-FRONTEND-HANDOFF
 * 2.3). Ids only: the server reads titles, position, kind and progress from
 * the course tables after an enrolment check. Like `viewing`, it is context
 * and never thread identity.
 */
export type ChatCourseContext = {
  /** The course id, not its slug. */
  courseId: string;
  /** Set on module pages and step screens. */
  moduleId?: string;
  /** Set on step screens. */
  stepId?: string;
};

/** Selected text is truncated to this many characters before it is sent. */
export const CHAT_SELECTION_MAX_CHARS = 2000;

/** Composer prefill is truncated to this many characters before it is sent. */
export const CHAT_PREFILL_MAX_CHARS = 2000;

/** The screen outline is truncated to this many characters before it is sent. */
export const CHAT_SCREEN_MAX_CHARS = 4000;

/**
 * Video positions are floored to this many seconds, so a playing video does not
 * rebuild and re-publish the envelope on every `timeupdate` tick. "What did she
 * just say" survives the coarsening; the anchor's exact position does not
 * matter to a tool that fetches the transcript around it.
 */
export const CHAT_VIDEO_TIMESTAMP_BUCKET_SECONDS = 5;
